import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { OrderDeliveryValidation } from '../_interfaces/delivery-validation';
import { OrderDispatchGroup } from '../_interfaces/dispatch-group';

export interface DeliveryValidationUpdateInput {
  validatedQty: number;
  cancel?: boolean;
  condition?: string;
  notes?: string;
}

export interface DeliveryValidationQueryParams {
  pageIndex: number;
  pageSize: number;
  orderId?: number;
  status?: string;
}

export interface DeliveryValidationQueryResult {
  items: OrderDeliveryValidation[];
  count: number;
}

export interface DeliveryQueueParams {
  pageIndex: number;
  pageSize: number;
  status?: string;
  // Comma-separated for multi-value search, e.g. "10234, 10240".
  orderNo?: string;
  search?: string;
  postCode?: string;
  deliveryMethod?: string;
  dispatchGroup?: string;
  dateField?: 'ORDER' | 'PAID' | 'VALIDATED' | 'DISPATCH_BY';
  dtFrom?: string;
  dtTo?: string;
  tab?: 'PROCESSING' | 'COMPLETED' | 'DELAYED' | 'VOID' | 'CANCELLED';
}

export interface DeliveryQueueResult {
  orders: any[];
  count: number;
}

export interface PurchaseReceiptLine {
  id: number;
  productName: string;
  sku: string | null;
  quantity: number;
  quantityReceived: number;
  outstanding: number;
}

// A container delivered to a yard, waiting to be checked in. Its lines are
// shipment lines, each tied to the PO line it books stock into.
export interface ContainerReceipt {
  id: number;
  containerNo: string | null;
  clearedAt: string | null;
  shipmentId: number;
  shipmentReference: string;
  store: string;
  shippingLine: string | null;
  vesselDetails: string | null;
  eta: string | null;
  portOfDischarge: string | null;
  // On the container page (any state): its state, and anything sent back.
  status?: string;
  etd?: string | null;
  portOfLoading?: string | null;
  returns?: { productName: string; sku: string | null; quantity: number; reason: string | null; createdAt: string }[];
  lines: {
    id: number;
    purchaseOrderId: number;
    poReference: string;
    poStatus: string | null;
    supplierName: string | null;
    productId: number | null;
    productName: string;
    sku: string | null;
    unitOfMeasure: string;
    expected: number;
    outstandingOnPo: number | null;
    onPo: boolean;
  }[];
}

export interface PurchaseReceipt {
  id: number;
  reference: string;
  store: string;
  status: string;
  expectedDeliveryDate: string | null;
  orderDate: string;
  supplierName: string | null;
  notes: string | null;
  items: PurchaseReceiptLine[];
}

export interface ProductDimensions {
  productVariantId: number;
  weight: number | null;
  length: number | null;
  width: number | null;
  height: number | null;
}

@Injectable({
  providedIn: 'root',
})
export class DeliveryValidationService {
  constructor(private http: HttpClient) {}

  listValidations(params: DeliveryValidationQueryParams): Observable<DeliveryValidationQueryResult> {
    return this.http.post<DeliveryValidationQueryResult>('/delivery-validation/list', params);
  }

  getOrCreateForOrder(orderId: number): Observable<OrderDeliveryValidation[]> {
    return this.http.get<OrderDeliveryValidation[]>(`/delivery-validation/order/${orderId}`);
  }

  updateValidation(id: number, input: DeliveryValidationUpdateInput): Observable<OrderDeliveryValidation> {
    return this.http.put<OrderDeliveryValidation>(`/delivery-validation/${id}`, input);
  }

  addSubstituteLine(
    orderId: number,
    input: {
      orderItemId: number;
      replaceLineId?: number | null;
      productVariantId: number;
      quantity: number;
      replacedQty: number;
      warehouse: string;
      notes?: string;
    },
  ): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(`/delivery-validation/order/${orderId}/substitute`, input);
  }

  getQueue(params: DeliveryQueueParams): Observable<DeliveryQueueResult> {
    return this.http.post<DeliveryQueueResult>('/delivery-validation/queue', params);
  }

  // Incoming stocks: POs on their way to the yards, and booking goods in.
  getReceipts(): Observable<PurchaseReceipt[]> {
    return this.http.post<PurchaseReceipt[]>('/delivery-validation/receipts', {});
  }

  // One container in any state, through the delivery side's access.
  getContainerForReceipt(id: number): Observable<ContainerReceipt> {
    return this.http.post<ContainerReceipt>(`/delivery-validation/receipts/containers/${id}`, {});
  }

  getContainerReceipts(): Observable<ContainerReceipt[]> {
    return this.http.post<ContainerReceipt[]>('/delivery-validation/receipts/containers', {});
  }

  // Checks a delivered container in: books what arrived into its POs and
  // marks it Received.
  // `damaged` is part of `quantity`: booked in, then written off as scrap.
  // `returned` is too: sent back, so not booked in - stays outstanding on the PO.
  receiveContainer(
    containerId: number,
    lines: { lineId: number; quantity: number; damaged?: number; damageNote?: string; returned?: number; returnNote?: string }[],
  ): Observable<{ success: boolean; units: number; damaged: number; returned: number; purchaseOrders: number; shipmentReference: string; containerNo: string | null }> {
    return this.http.post<any>(`/delivery-validation/receipts/containers/${containerId}/receive`, { lines });
  }

  receiveReceipt(purchaseOrderId: number, lines: { itemId: number; quantity: number }[]): Observable<any> {
    return this.http.post<any>(`/delivery-validation/receipts/${purchaseOrderId}/receive`, { lines });
  }

  // Order count per tab under the given filters (tab/paging ignored).
  getQueueCounts(params: Partial<DeliveryQueueParams>): Observable<Record<string, number>> {
    return this.http.post<Record<string, number>>('/delivery-validation/queue-counts', params);
  }

  // Order id + number of every order in the queue - for the Previous/Next
  // navigator, which moves between /delivery-validation/<order no> addresses.
  getQueueOrderRefs(params: DeliveryQueueParams): Observable<{ orderId: number; orderNo: string }[]> {
    return this.http.post<{ orderId: number; orderNo: string }[]>('/delivery-validation/queue-order-refs', params);
  }

  // The queue tab + filters the list was showing when an order was opened,
  // so Previous / Next on the detail screen move through that same list.
  queueContext: DeliveryQueueParams | null = null;

  // Order numbers seen so far, so opening an order from the list or with
  // Previous/Next doesn't need a lookup.
  private orderIdByNo = new Map<string, number>();

  rememberOrder(orderId: number, orderNo: string | number) {
    this.orderIdByNo.set(String(orderNo), Number(orderId));
  }

  resolveOrderId(orderNo: string): Observable<number> {
    const known = this.orderIdByNo.get(orderNo);
    if (known) return of(known);
    return this.http
      .post<{ orderId: number; orderNo: string }>('/orders/id-by-number', { orderNo })
      .pipe(
        map((r) => {
          this.rememberOrder(r.orderId, r.orderNo);
          return r.orderId;
        }),
      );
  }

  // Ids only - for the Previous/Next navigator on the order detail screen.
  getQueueOrderIds(params: DeliveryQueueParams): Observable<number[]> {
    return this.http.post<number[]>('/delivery-validation/queue-ids', params);
  }

  getProductDimensions(variantIds: number[]): Observable<ProductDimensions[]> {
    return this.http.post<ProductDimensions[]>('/delivery-validation/product-dimensions', { variantIds });
  }

  getDispatchGroups(orderIds: number[]): Observable<OrderDispatchGroup[]> {
    return this.http.post<OrderDispatchGroup[]>('/delivery-validation/dispatch-groups', { orderIds });
  }

  getDispatchGroup(orderId: number): Observable<OrderDispatchGroup | null> {
    return this.http.get<OrderDispatchGroup | null>(`/delivery-validation/order/${orderId}/dispatch-group`);
  }

  setDispatchGroup(orderId: number, dispatchGroup: string, notes?: string): Observable<OrderDispatchGroup> {
    return this.http.put<OrderDispatchGroup>(`/delivery-validation/order/${orderId}/dispatch-group`, {
      dispatchGroup,
      notes,
    });
  }

  bulkSetDispatchGroup(orderIds: number[], dispatchGroup: string, notes?: string): Observable<OrderDispatchGroup[]> {
    return this.http.put<OrderDispatchGroup[]>('/delivery-validation/dispatch-groups/bulk', {
      orderIds,
      dispatchGroup,
      notes,
    });
  }
}
